import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function StartAssessmentDialog({ replacingDraft, canCarryForward, canRefresh, onRefresh, onConfirm, onClose, returnFocusRef }) {
  const [mode, setMode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const cancelRef = useRef(null);

  async function confirm() {
    if (!mode || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try { await onConfirm(mode); onClose(); }
    catch (cause) { setError(cause?.message || 'The assessment could not be started. Try again.'); }
    finally { busyRef.current = false; setBusy(false); }
  }

  return <Dialog open onOpenChange={open => { if (!open && !busyRef.current) onClose(); }}>
    <DialogContent role="alertdialog" hideClose returnFocusRef={returnFocusRef} className="assessment-panel max-h-[90dvh] overflow-y-auto"
      onOpenAutoFocus={event => { event.preventDefault(); cancelRef.current?.focus(); }}
      onCloseAutoFocus={event => { if (busyRef.current) event.preventDefault(); }}>
      <DialogHeader>
        <DialogTitle>Start a new assessment?</DialogTitle>
        <DialogDescription>Your new assessment uses the current framework. Previous assessments, Roadmap work and evidence will be kept.</DialogDescription>
      </DialogHeader>
      {replacingDraft && <p>Your current draft will become read-only history. Pending DI reviews of this draft will be withdrawn.</p>}
      <fieldset disabled={busy} className="space-y-3">
        <legend className="font-semibold mb-2">Choose how to start</legend>
        <label className="flex min-h-11 items-center gap-3"><input type="radio" name="assessment-start-mode" value="blank" checked={mode === 'blank'} onChange={() => setMode('blank')} />Start with blank answers</label>
        <p className="text-sm">All tasks in the new assessment will be unanswered.</p>
        <label className="flex min-h-11 items-center gap-3"><input type="radio" name="assessment-start-mode" value="carry_forward" disabled={!canCarryForward} checked={mode === 'carry_forward'} onChange={() => setMode('carry_forward')} />Carry forward compatible answers</label>
        <p className="text-sm">{canCarryForward ? 'Copy answers from your current draft or latest submission only where the question and tasks are unchanged. Review them before submitting.' : 'There are no current draft or submitted answers to carry forward.'}</p>
      </fieldset>
      {error && <div className="space-y-2">
        <p role="alert" className="accessible-error text-destructive">{error}</p>
        {canRefresh && <><p>Refresh to check the latest saved assessment before trying again.</p><button type="button" disabled={busy} onClick={onRefresh} className="min-h-11 rounded-lg border border-border px-4">Refresh assessment</button></>}
      </div>}
      {busy && <p role="status">Starting your assessment…</p>}
      <DialogFooter className="gap-2">
        <button ref={cancelRef} type="button" disabled={busy} onClick={onClose} className="min-h-11 rounded-lg border border-border px-4">Cancel</button>
        <button type="button" disabled={busy || !mode} onClick={confirm} className="min-h-11 rounded-lg bg-primary px-4 font-semibold text-primary-foreground">Start new assessment</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
